import { computed, toRef, watch } from 'vue';
import { useAcl, useI18n, usePlanLimits } from '@/composables';
import { useFlowPathFieldErrors } from '@/composables/useFlowDialogFieldErrors';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';

type FlowPathItem = {
  name?: string;
  weight: number;
  status?: string;
  directLinking?: boolean;
  landings: Array<{ id: string; weight?: number; offers?: number }>;
  offers: Array<{ id: string; weight: number }>;
};

type CampaignFlowPathProps = {
  path: FlowPathItem;
  isDisabledWeight: boolean;
  paths: FlowPathItem[];
  ruleIndex: number;
  pathIndex: number;
  form: Record<string, unknown> & {
    workspaceId: string;
    rotation: string;
  };
  countryId: string;
  errorConditionPrefix: string[];
  dialogRepositionParam: string;
  errorsRaw: unknown[][] | null;
  offerOptions: Offer[];
  landingOptions: Landing[];
};

const landingDisabledInStateActive = ['fixOnLanding', 'fixOnOffers', 'landings', 'landingsAndOffers'];

export const useCampaignFlowPath = (props: CampaignFlowPathProps) => {
  const { t } = useI18n();
  const store = useStore();
  const { aclCanCreateOnSection } = useAcl();
  const offersPlanLimits = usePlanLimits('offersAmount');
  const landingsPlanLimits = usePlanLimits('landingsAmount');

  const reportingFlowRulePathEnabled = computed(
    () => store.user.featureFlags?.reportingFlowRulePath?.enabled,
  );

  const errorCondition = computed(() => [
    ...props.errorConditionPrefix,
    'rules',
    props.ruleIndex + 1,
    'paths',
    props.pathIndex + 1,
  ]);

  const {
    isEmptyLandingsError,
    isEmptyOffersError,
    weightErrorMessages,
  } = useFlowPathFieldErrors({
    errorsRaw: toRef(props, 'errorsRaw'),
    errorCondition,
  });

  const activeSwitchDisabled = computed(() => {
    const weight = Number(props.path.weight);
    return props.path.weight <= 0 || Number.isNaN(weight);
  });

  const active = computed({
    get: () => (activeSwitchDisabled.value ? 'inactive' : props.path.status),
    set: (value: string) => {
      props.path.status = value;
    },
  });

  const offersCount = computed(() => props.path.landings?.[0]?.offers || 0);

  const currentOffersCount = computed(() => props.path.offers.length);

  const totalWeightOfLandings = computed(() =>
    props.path.landings
      .map((item) => +item.weight)
      .reduce((accumulator, item) => accumulator + item, 0),
  );

  const totalWeightOfOffers = computed(() =>
    props.path.offers
      .map((item) => +item.weight)
      .reduce((accumulator, item) => accumulator + item, 0),
  );

  const hasPlanLimitsRestriction = (limitKey: 'offersAmount' | 'landingsAmount') => {
    if (limitKey === 'offersAmount') {
      return offersPlanLimits.hasPlanLimitsRestriction(limitKey);
    }
    return landingsPlanLimits.hasPlanLimitsRestriction(limitKey);
  };

  const getGetOffer = (offerCount = 1) =>
    new Array(offerCount).fill(null).map(() => ({
      id: '',
      weight: 100,
    }));

  const addOffer = (offerCount = 1) => {
    const newOffers = getGetOffer(offerCount);
    props.path.offers.push(...newOffers);
  };

  const addNewOffer = () => {
    if (offersPlanLimits.hasPlanLimitsRestriction('offersAmount')) {
      return offersPlanLimits.handleClickPlanLimits('offersAmount');
    }

    $emitter.emit('campaign-new-offer');
  };

  const editOffer = (id: string) => {
    $emitter.emit('campaign-edit-offer', id);
  };

  const previewOffer = (url: string) => {
    window.open(url, '_blank');
  };

  const delOffer = (offerIndex: number, count = 1) => {
    props.path.offers.splice(offerIndex, count);
    $emitter.emit('campaign-delete-offer', props.ruleIndex, props.pathIndex, offerIndex);
  };

  const addLanding = (landing: { id: string } = { id: '' }) => {
    props.path.landings.push({
      ...landing,
      weight: 100,
    });
  };

  const addNewLanding = () => {
    if (landingsPlanLimits.hasPlanLimitsRestriction('landingsAmount')) {
      return landingsPlanLimits.handleClickPlanLimits('landingsAmount');
    }

    $emitter.emit('campaign-new-landing');
  };

  const editLanding = (id: string) => {
    $emitter.emit('campaign-edit-landing', id);
  };

  const previewLanding = (url: string) => {
    window.open(url, '_blank');
  };

  const delLanding = (landingIndex: number) => {
    props.path.landings.splice(landingIndex, 1);
    $emitter.emit('campaign-delete-landing', props.ruleIndex, props.pathIndex, landingIndex);
  };

  const fixLandingCount = (selectLanding: { offers: number }, preserveLandings = false) => {
    if (!preserveLandings) {
      props.path.landings = [];
      addLanding(selectLanding);
    }

    const deltaOffers = currentOffersCount.value - selectLanding.offers;

    if (deltaOffers < 0) {
      addOffer(Math.abs(deltaOffers));
    } else {
      delOffer(selectLanding.offers, Math.abs(deltaOffers));
    }
  };

  const countWeight = (
    item: { weight: number; status?: string },
    weights: { weight: number; status?: string }[],
  ) => {
    let percent = 0;

    if (Number(item) !== 0) {
      let sum = 0;
      weights.forEach((weightItem) => {
        if (weightItem.status === 'inactive') {
          return;
        }

        sum = sum + +weightItem.weight;
      });

      if (sum !== 0) {
        percent = (item.weight * 100) / sum;
      }
    }

    return Math.round(percent);
  };

  const deletePath = () => {
    $emitter.emit('campaign-delete-path', props.ruleIndex, props.pathIndex);
  };

  const translatedName = (name: string) => name.replace('Path', t('Path'));

  watch(
    () => props.form.rotation,
    (value) => {
      if (landingDisabledInStateActive.includes(value)) {
        props.path.directLinking = false;
      }
    },
  );

  return {
    t,
    reportingFlowRulePathEnabled,
    landingDisabledInStateActive,
    activeSwitchDisabled,
    active,
    offersCount,
    totalWeightOfLandings,
    totalWeightOfOffers,
    isEmptyLandingsError,
    isEmptyOffersError,
    weightErrorMessages,
    hasPlanLimitsRestriction,
    aclCanCreateOnSection,
    addOffer,
    addNewOffer,
    editOffer,
    previewOffer,
    delOffer,
    addLanding,
    addNewLanding,
    editLanding,
    previewLanding,
    delLanding,
    fixLandingCount,
    countWeight,
    deletePath,
    translatedName,
  };
};
